<script>
import PaginationController from '@/components/base-components/PaginationController.vue';
import constants from '@/constants';

export default {
    components: {
        PaginationController,
    },
    computed: {
        notifications() {
            return this.$store.getters.ANTIFRAUD_NOTIFICATIONS;
        },
        pagination() {
            return this.$store.getters.ANTIFRAUD_PAGINATION;
        },
        isLoading() {
            return this.$store.getters.IS_ANTIFRAUD_LOADING;
        },
        currentPage() {
            return this.$store.getters.ANTIFRAUD_CURRENT_PAGE;
        },
        maxPage() {
            return this.$store.getters.ANTIFRAUD_TOTAL_PAGES;
        },
        displayRange() {
            return this.$store.getters.ANTIFRAUD_DISPLAY_RANGE;
        },
        showPagination() {
            return this.pagination.total > this.pagination.limit;
        },
    },
    methods: {
        async handlePageChange(action) {
            await this.$store.dispatch('changeAntifraudPage', action);
        },
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';
            return date.toLocaleString('en-US', {
                timeZone: 'Europe/Minsk',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            });
        },
        formatMeta(meta) {
            if (!meta) return 'N/A';
            if (typeof meta === 'string') return meta;
            return JSON.stringify(meta);
        },
    },
};
</script>

<template>
    <div class="right_container_block">
        <div class="right_container_block__header">
            <h2>Antifraud Notifications</h2>
        </div>

        <div v-if="isLoading" class="loading_container">
            <p>Loading notifications...</p>
        </div>

        <div v-else-if="notifications.length === 0" class="no_notifications">
            <p>No antifraud notifications found</p>
        </div>

        <div v-else class="notifications_wrapper">
            <div class="pagination_info">
                <span>{{ displayRange }} notifications</span>
            </div>

            <div class="notifications_table_wrapper">
                <table class="notifications_table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>User</th>
                            <th>Type</th>
                            <th>Created</th>
                            <th>Meta</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="notification in notifications"
                            :key="notification.id"
                        >
                            <td>{{ notification.id }}</td>
                            <td class="user_cell">
                                <router-link
                                    v-if="notification.user_id"
                                    :to="{
                                        name: 'userEditWithId',
                                        params: {
                                            userId: notification.user_id,
                                        },
                                    }"
                                    class="user_link"
                                    target="_blank"
                                >
                                    {{ notification.user_id }}
                                </router-link>
                                <span v-else>N/A</span>
                            </td>
                            <td>
                                <span class="notification_type">{{
                                    notification.notification_type || 'N/A'
                                }}</span>
                            </td>
                            <td>{{ formatDate(notification.created_at) }}</td>
                            <td class="meta_cell">
                                {{ formatMeta(notification.meta) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <PaginationController
                v-if="showPagination"
                :currentPage="currentPage"
                :maxPage="maxPage"
                @first="handlePageChange('first')"
                @back="handlePageChange('prev')"
                @next="handlePageChange('next')"
                @last="handlePageChange('last')"
            />
        </div>
    </div>
</template>

<style scoped>
.right_container_block {
    padding: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.right_container_block__header {
    margin-bottom: 20px;
}

.right_container_block__header h2 {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: var(--text-color);
}

.loading_container,
.no_notifications {
    padding: 40px 20px;
    text-align: center;
    color: var(--text-color-secondary);
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
}

.notifications_wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
    overflow: hidden;
}

.pagination_info {
    margin-bottom: 10px;
    color: var(--text-color-secondary);
    font-size: 14px;
}

.notifications_table_wrapper {
    flex: 1;
    overflow-y: auto;
    overflow-x: auto;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    background: var(--background);
}

.notifications_table {
    width: 100%;
    border-collapse: collapse;
    min-width: 1000px;
}

.notifications_table thead {
    position: sticky;
    top: 0;
    background: var(--background);
    z-index: 1;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.notifications_table th {
    padding: 12px 15px;
    text-align: left;
    font-weight: 600;
    color: var(--text-color);
    border-bottom: 2px solid var(--border-color);
    white-space: nowrap;
}

.notifications_table td {
    padding: 12px 15px;
    border-bottom: 1px solid var(--border-color);
    color: var(--text-color-primary);
    background: var(--background);
}

.notifications_table tbody tr:hover td {
    background: var(--background-hover);
    transition: background 0.2s ease;
}

.user_cell {
    font-weight: 500;
}

.user_link {
    color: var(--main-color);
    text-decoration: none;
    font-weight: 600;
    transition: all 0.2s ease;
}

.user_link:hover {
    text-decoration: underline;
    opacity: 0.8;
}

.notification_type {
    display: inline-block;
    padding: 4px 12px;
    background: rgba(100, 150, 200, 0.2);
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    white-space: nowrap;
}

.meta_cell {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: monospace;
    font-size: 12px;
    color: var(--text-color-secondary);
}
</style>
